/**
 * @fileoverview SDK全局配置管理
 * @description 提供统一的配置管理，让所有组件和工具都能使用相同的配置
 * @author wangzhongsheng
 * @version 1.0.0
 */

/**
 * 默认配置
 */
const DEFAULT_SDK_CONFIG = {
  // baseURL: 'https://pdb.bjchy.gov.cn/mdb2/webapi',
  baseURL: 'https://pdb.bjchy.gov.cn/mdb3/api',
  timeout: 10000,
  tokenKey: 'bjchy_token',
  userKey:'bjchy_user_info',
  enableAutoDetectStreet: true,
  enableTokenAutoRefresh: true,
  loginUrl: '/', // token过期后跳转的页面，默认为 '/'
  tokenErrorCodes: [ // 需要触发登录跳转的token错误码
    'TOKEN_EXPIRED',      // Token已过期
    'INVALID_TOKEN',      // Token无效
    'TOKEN_MALFORMED',    // Token格式错误
    'TOKEN_NOT_ACTIVE',   // Token尚未生效
    'MISSING_TOKEN',      // Token缺失
    'MISSING_AUTH_HEADER', // 缺少授权头
    'INVALID_AUTH_FORMAT', // 授权头格式错误
    'TOKEN_EXTRACTION_ERROR' // Token提取失败
  ]
}

/**
 * 当前SDK配置
 */
let currentConfig = { ...DEFAULT_SDK_CONFIG }

/**
 * 配置变更监听器
 */
const configListeners = new Set()

/**
 * 设置SDK全局配置
 * @param {Object} config - 配置对象
 * @param {string} [config.baseURL] - API基础URL
 * @param {number} [config.timeout] - 请求超时时间
 * @param {string} [config.tokenKey] - token存储键名
 * userKey - userinfo存储的名
 * @param {boolean} [config.enableAutoDetectStreet] - 是否启用自动街道检测
 * @param {boolean} [config.enableTokenAutoRefresh] - 是否启用token自动刷新
 * @param {string} [config.loginUrl] - token过期后跳转的页面，默认为 '/'
 * @param {string[]} [config.tokenErrorCodes] - 需要触发登录跳转的token错误码数组
 */
export function configureSDK(config = {}) {
  console.log('📝 配置SDK:', config)
  
  // 合并配置
  const newConfig = { ...currentConfig, ...config }
  
  // 验证配置
  if (config.baseURL && typeof config.baseURL !== 'string') {
    throw new Error('baseURL 必须是字符串类型')
  }
  
  if (config.timeout && (typeof config.timeout !== 'number' || config.timeout <= 0)) {
    throw new Error('timeout 必须是正数')
  }
  
  // 更新配置
  const oldConfig = { ...currentConfig }
  currentConfig = newConfig
  
  // 通知所有监听器配置已变更
  configListeners.forEach(listener => {
    try {
      listener(newConfig, oldConfig)
    } catch (error) {
      console.error('配置监听器执行失败:', error)
    }
  })
  
  console.log('✅ SDK配置已更新:', currentConfig)
  
  return currentConfig
}

/**
 * 获取当前SDK配置
 * @returns {Object} 当前配置
 */
export function getSDKConfig() {
  return { ...currentConfig }
}

/**
 * 重置SDK配置为默认值
 */
export function resetSDKConfig() {
  console.log('🔄 重置SDK配置为默认值')
  configureSDK(DEFAULT_SDK_CONFIG)
}

/**
 * 添加配置变更监听器
 * @param {Function} listener - 监听器函数，接收 (newConfig, oldConfig) 参数
 */
export function addConfigListener(listener) {
  if (typeof listener !== 'function') {
    throw new Error('监听器必须是函数')
  }
  configListeners.add(listener)
}

/**
 * 移除配置变更监听器
 * @param {Function} listener - 要移除的监听器函数
 */
export function removeConfigListener(listener) {
  configListeners.delete(listener)
}

/**
 * 获取特定配置项
 * @param {string} key - 配置键名
 * @returns {*} 配置值
 */
export function getConfigValue(key) {
  return currentConfig[key]
}

/**
 * 检查配置是否有效
 * @returns {Object} 验证结果 { valid: boolean, errors: string[] }
 */
export function validateConfig() {
  const errors = []
  
  if (!currentConfig.baseURL) {
    errors.push('baseURL 不能为空')
  } else if (typeof currentConfig.baseURL !== 'string') {
    errors.push('baseURL 必须是字符串')
  } else if (!currentConfig.baseURL.startsWith('http')) {
    errors.push('baseURL 必须以 http:// 或 https:// 开头')
  }
  
  if (currentConfig.timeout <= 0) {
    errors.push('timeout 必须大于 0')
  }
  
  if (!currentConfig.tokenKey || typeof currentConfig.tokenKey !== 'string') {
    errors.push('tokenKey 不能为空且必须是字符串')
  }
  if (!currentConfig.userKey || typeof currentConfig.userKey !== 'string') {
    errors.push('userKey 不能为空且必须是字符串')
  }
  
  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * 打印当前配置信息（调试用）
 */
export function printConfig() {
  console.group('🔧 当前SDK配置')
  Object.entries(currentConfig).forEach(([key, value]) => {
    console.log(`${key}:`, value)
  })
  console.groupEnd()
} 